import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '../../../shared/components/Alert';
import { PageContainer } from '../../../shared/components/PageContainer';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Skeleton } from '../../../shared/components/Skeleton';
import { useFilaAprovacoes } from '../hooks';
import { JOB_TYPE_LABELS, formatCurrencyBRL, formatDateBR } from '../components/format';
import { DevolucoesBadge } from '../components/RecrutamentoBadges';

/**
 * Fila de aprovações do usuário.
 *
 * Traz somente as requisições cujo **passo corrente** ele pode decidir —
 * quem resolve isso é o servidor, cruzando o passo pendente com as
 * features do perfil. A tela não filtra nada por conta própria: fazer
 * isso aqui significaria reimplementar a cadeia no cliente.
 */
export default function FilaAprovacoesPage(): React.ReactElement {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useFilaAprovacoes({ limit: 50 });
  const requisicoes = data?.data ?? [];

  return (
    <PageContainer
      title="Aprovações pendentes"
      subtitle="Requisições de vaga aguardando a sua decisão"
    >
      {isError && <Alert variant="error">Não foi possível carregar a fila de aprovações.</Alert>}

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : requisicoes.length === 0 ? (
        <EmptyState
          title="Nenhuma aprovação pendente"
          description="Quando uma requisição chegar ao seu passo da cadeia, ela aparece aqui."
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Código</th>
                <th className="px-3 py-2 text-left">Cargo</th>
                <th className="px-3 py-2 text-left">Área</th>
                <th className="px-3 py-2 text-right">Vagas</th>
                <th className="px-3 py-2 text-left">Tipo</th>
                <th className="px-3 py-2 text-right">Salário previsto</th>
                <th className="px-3 py-2 text-left">Enviada em</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {requisicoes.map((requisicao) => (
                <tr
                  key={requisicao.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => navigate(`/recrutamento/requisicoes/${requisicao.id}`)}
                >
                  <td className="px-3 py-2 font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      {requisicao.code}
                      <DevolucoesBadge count={requisicao.returnCount} />
                    </div>
                  </td>
                  <td className="px-3 py-2">{requisicao.positionTitle}</td>
                  <td className="px-3 py-2">{requisicao.requestingArea}</td>
                  <td className="px-3 py-2 text-right">{requisicao.vacanciesCount}</td>
                  <td className="px-3 py-2">{JOB_TYPE_LABELS[requisicao.jobType]}</td>
                  <td className="px-3 py-2 text-right">
                    {formatCurrencyBRL(requisicao.expectedSalary)}
                  </td>
                  <td className="px-3 py-2">{formatDateBR(requisicao.submittedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageContainer>
  );
}
